import * as React from 'react'

declare global {
  interface Window {
    ClassicEditor?: any
    DecoupledEditor?: any
    CKEDITOR?: any
  }
}

export type RichTextEditorProps = {
  value: string
  onChange: (html: string) => void
  className?: string
  height?: number
  placeholder?: string
  toolbar?: string[]
  /**
   * Callback opcional acionado quando o usuário pressiona apenas Enter.
   * Se estiver definido, o Enter envia a mensagem (ou executa a ação)
   * e Shift+Enter continua inserindo uma nova linha no editor.
   */
  onEnterPress?: () => void
  /**
   * Callback opcional disparado quando o editor CKEditor
   * estiver pronto para uso (ex.: integrações com toolbars customizadas).
   */
  onReady?: (editor: any) => void
  onPasteFiles?: (files: File[]) => void
}

let injectedThemeCss = false
let ckeditorScriptPromise: Promise<boolean> | null = null

const CKEDITOR_DECOUPLED_BUILD_SRC =
  'https://cdn.ckeditor.com/ckeditor5/41.4.2/decoupled-document/ckeditor.js'
const CKEDITOR_CLASSIC_BUILD_SRC = 'https://cdn.ckeditor.com/ckeditor5/41.4.2/classic/ckeditor.js'

type CkeditorBuild = 'decoupled-document' | 'classic'

function getDecoupledEditorCtor(): any | null {
  return window.CKEDITOR?.DecoupledEditor || window.DecoupledEditor || null
}

function getClassicEditorCtor(): any | null {
  return window.ClassicEditor || null
}

function resolveEditorCtor(): { ctor: any; build: CkeditorBuild } | null {
  const decoupled = getDecoupledEditorCtor()
  if (decoupled) return { ctor: decoupled, build: 'decoupled-document' }
  const classic = getClassicEditorCtor()
  if (classic) return { ctor: classic, build: 'classic' }
  return null
}

function hasDecoupledBuild(): boolean {
  return !!getDecoupledEditorCtor()
}

function hasClassicBuild(): boolean {
  return !!getClassicEditorCtor()
}

function ensureCkeditorScript(): Promise<boolean> {
  if (hasDecoupledBuild() || hasClassicBuild()) return Promise.resolve(true)
  if (ckeditorScriptPromise) return ckeditorScriptPromise

  const load = (src: string, tag: string) =>
    new Promise<boolean>((resolve) => {
      try {
        const existing = document.querySelector(
          `script[data-ckeditor-build=\"${tag}\"]`,
        ) as HTMLScriptElement | null

        if (existing) {
          if (tag === 'decoupled-document' && hasDecoupledBuild()) return resolve(true)
          if (tag === 'classic' && hasClassicBuild()) return resolve(true)

          existing.addEventListener('load', () => resolve(true), { once: true })
          existing.addEventListener('error', () => resolve(false), { once: true })
          return
        }
      } catch {
        // ignore
      }

      const s = document.createElement('script')
      s.src = src
      s.async = true
      s.setAttribute('data-ckeditor-build', tag)
      s.onload = () => resolve(true)
      s.onerror = () => resolve(false)
      document.head.appendChild(s)
    })

  ckeditorScriptPromise = (async () => {
    const decoupledOk = await load(CKEDITOR_DECOUPLED_BUILD_SRC, 'decoupled-document')
    if (decoupledOk && hasDecoupledBuild()) return true
    const classicOk = await load(CKEDITOR_CLASSIC_BUILD_SRC, 'classic')
    if (classicOk && hasClassicBuild()) return true
    return false
  })()

  return ckeditorScriptPromise
}

const DEFAULT_TOOLBAR: string[] = [
  'heading',
  '|',
  'bold',
  'italic',
  'link',
  'bulletedList',
  'numberedList',
  'blockQuote',
  '|',
  'undo',
  'redo',
]

export function RichTextEditor({
  value,
  onChange,
  className,
  height = 420,
  placeholder,
  toolbar,
  onEnterPress,
  onReady,
  onPasteFiles,
}: RichTextEditorProps) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const toolbarHostRef = React.useRef<HTMLDivElement | null>(null)
  const editorRef = React.useRef<any>(null)
  const fallbackTextareaRef = React.useRef<HTMLTextAreaElement | null>(null)
  const [ready, setReady] = React.useState(false)
  const [initError, setInitError] = React.useState<string | null>(null)
  const baseHeightRef = React.useRef<number | null>(null)

  const onEnterPressRef = React.useRef<(() => void) | undefined>(onEnterPress)
  const onReadyRef = React.useRef<((editor: any) => void) | undefined>(onReady)
  const onPasteFilesRef =
    React.useRef<((files: File[]) => void) | undefined>(undefined)

  React.useEffect(() => {
    onEnterPressRef.current = onEnterPress
  }, [onEnterPress])

  React.useEffect(() => {
    onReadyRef.current = onReady
  }, [onReady])

  React.useEffect(() => {
    onPasteFilesRef.current = onPasteFiles
  }, [onPasteFiles])

  React.useEffect(() => {
    let cancelled = false
    let detachPasteListener: (() => void) | null = null
    let detachDropListener: (() => void) | null = null

    async function ensureScript() {
      return ensureCkeditorScript()
    }

    ;(async () => {
      // CSS global do CKEditor alinhado ao tema da aplicação
      if (!injectedThemeCss) {
        const style = document.createElement('style')
        style.setAttribute('data-ck-theme', 'overrides')
        style.innerHTML = `
          /* Base editor area (sem min-height fixo; altura vem da prop height) */
          .ck.ck-editor__editable_inline {
            background: var(--background);
            color: var(--foreground);
          }

          /* Barra de ferramentas e cabeçalho - tema escuro seguindo o Cripto Alert */
          .ck.ck-toolbar,
          .ck.ck-dropdown__panel,
          .ck.ck-list__item {
            background: var(--background);
            color: var(--foreground);
          }

          html:not(.light) .ck.ck-editor,
          html:not(.light) .ck.ck-editor__top,
          html:not(.light) .ck.ck-sticky-panel,
          html:not(.light) .ck.ck-sticky-panel__content,
          html:not(.light) .ck.ck-toolbar {
            background-color: var(--background) !important;
            border-color: var(--border) !important;
          }

          html:not(.light) .ck.ck-editor__main,
          html:not(.light) .ck.ck-editor__editable,
          html:not(.light) .ck.ck-editor__editable_inline {
            background-color: var(--background) !important;
            border-color: var(--border) !important;
          }

          html.light .ck.ck-toolbar,
          html.light .ck.ck-dropdown__panel {
            border-color: rgba(0,0,0,0.12);
          }

          .ck.ck-button {
            color: var(--foreground);
          }

          .ck.ck-button.ck-on,
          .ck.ck-button:hover {
            background: rgba(125,125,125,0.15);
          }

          /* Ensure editor content area follows app theme */
          .ck-content,
          .ck.ck-editor__main,
          .ck.ck-editor__editable,
          .ck.ck-editor__editable_inline {
            background: var(--background) !important;
            color: var(--foreground) !important;
          }

           .ck-content p,
           .ck-content li {
             color: var(--foreground) !important;
           }

           .ck-content ul,
           .ck-content ol {
             padding-left: 1.25rem;
             margin: 0;
           }

           .ck-content ul {
             list-style-type: disc;
           }

           .ck-content ol {
             list-style-type: decimal;
           }

           .ck-content li {
             margin: 0;
           }

           .ck-content a {
             color: var(--primary) !important;
             text-decoration: underline;
           }

          .ck-content a:hover {
            opacity: .9;
          }

          /* Guarantee panels appear above app overlays */
          .ck.ck-balloon-panel,
          .ck.ck-dropdown__panel,
          .ck.ck-tooltip {
            z-index: 100010 !important;
            pointer-events: auto;
          }

          /* Editor de comunidade legado: altura fixa de 150px na área de digitação */
          .community-editor .ck.ck-editor__editable_inline {
            height: 150px !important;
            min-height: 150px !important;
            max-height: 150px !important;
            overflow-y: auto !important;
          }

          /* Decoupled document build: toolbar vive fora do editor e deve ser inserida em um host */
          .ck-toolbar-host {
            margin-bottom: 0.5rem;
          }

          .ck-toolbar-host:empty {
            display: none;
          }

          .message-editor .ck-toolbar-host {
            display: none !important;
          }

          /* MessageEditor (chat): esconde toolbar nativa e badge "Powered by CKEditor" */
          .message-editor .ck.ck-toolbar {
            display: none !important;
          }

          .message-editor .ck.ck-editor__top {
            display: none !important;
            border: 0 !important;
            box-shadow: none !important;
          }

           .message-editor .ck.ck-powered-by {
             display: none !important;
           }

           /* MessageEditor (chat) - variante Slack: remove bordas internas do CKEditor */
           .message-editor--slack .ck.ck-editor,
           .message-editor--slack .ck.ck-editor__main,
           .message-editor--slack .ck.ck-editor__editable,
           .message-editor--slack .ck.ck-editor__editable_inline {
             border: 0 !important;
             box-shadow: none !important;
           }

           .message-editor--slack .ck.ck-editor__editable_inline {
             padding: 6px 6px 0 6px !important;
           }

           /* Fallback global: remove badge de "Powered by CKEditor" em qualquer contexto */
           .ck.ck-powered-by {
             display: none !important;
           }
        `
        document.head.appendChild(style)
        injectedThemeCss = true
      }

      setInitError(null)
      const ok = await ensureScript()
      if (cancelled) return
      if (!ok) {
        setInitError('Não foi possível carregar o CKEditor.')
        return
      }

      const resolved = resolveEditorCtor()
      if (!resolved?.ctor) {
        setInitError('CKEditor carregou, mas o construtor não foi encontrado.')
        return
      }

      if (ref.current) {
        try {
          const requestedToolbar =
            toolbar && toolbar.length ? toolbar : DEFAULT_TOOLBAR

          const createConfig = {
            licenseKey: 'GPL',
            toolbar: requestedToolbar,
            link: {
              addTargetToExternalLinks: true,
              defaultProtocol: 'https://',
            },
            placeholder: placeholder || 'Digite os termos aqui...',
          } as any

          try {
            editorRef.current = await resolved.ctor.create(ref.current, createConfig)
          } catch (err: any) {
            // Se a toolbar solicitada tiver itens que não existirem no build carregado,
            // faz fallback para a toolbar default (compatível com classic-build).
            try {
              editorRef.current = await resolved.ctor.create(ref.current, {
                ...createConfig,
                toolbar: DEFAULT_TOOLBAR,
              } as any)
            } catch (err2: any) {
              const message = (() => {
                try {
                  const raw = err2?.message ? String(err2.message) : String(err2)
                  return raw && raw !== '[object Object]'
                    ? raw
                    : 'Falha ao inicializar o editor.'
                } catch {
                  return 'Falha ao inicializar o editor.'
                }
              })()

              if (!cancelled) {
                setInitError(message)
              }

              if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.error('[RichTextEditor] CKEditor init error:', err2)
              }

              throw err2 || err
            }
          }

          if (resolved.build === 'decoupled-document') {
            try {
              const host = toolbarHostRef.current
              const toolbarEl = editorRef.current?.ui?.view?.toolbar
                ?.element as HTMLElement | null
              const isMessageEditor = !!ref.current?.closest?.('.message-editor')

              if (host && toolbarEl && !isMessageEditor) {
                while (host.firstChild) host.removeChild(host.firstChild)
                host.appendChild(toolbarEl)
              }
            } catch {
              // ignore
            }
          }

          setReady(true)
          editorRef.current.setData(value || '')

          try {
            const editableEl = editorRef.current?.ui?.view?.editable
              ?.element as HTMLElement | null

            if (editableEl) {
              const applyFixedHeight = () => {
                const hostMessageEditor = editableEl.closest('.message-editor')

                if (hostMessageEditor) {
                  const min = height || 100
                  const max = Math.max(min * 3, min + 120)
                  editableEl.style.minHeight = `${min}px`
                  editableEl.style.height = 'auto'
                  editableEl.style.maxHeight = `${max}px`
                  editableEl.style.overflowY = 'auto'
                  return
                }

                if (!baseHeightRef.current) {
                  const rect = editableEl.getBoundingClientRect()
                  if (rect && rect.height) {
                    baseHeightRef.current = rect.height
                  }
                }

                const h = height || baseHeightRef.current || 0
                if (!h) return

                editableEl.style.height = `${h}px`
                editableEl.style.minHeight = `${h}px`
                editableEl.style.maxHeight = `${h}px`
                editableEl.style.overflowY = 'auto'
              }

              // Aguarda um tick para o CKEditor finalizar o layout antes de medir
              applyFixedHeight()
              setTimeout(applyFixedHeight, 0)

              const removeInlineImagesIfAny = () => {
                const editor = editorRef.current
                if (!editor) return

                try {
                  const model = editor.model
                  if (!model || !model.document) return

                  model.change((writer: any) => {
                    const root = model.document.getRoot()
                    const range = model.createRangeIn(root)
                    const toRemove: any[] = []

                    for (const item of range.getItems()) {
                      if (!item || typeof item.is !== 'function') continue
                      if (item.is('element', 'imageBlock') || item.is('element', 'imageInline')) {
                        toRemove.push(item)
                      }
                    }

                    toRemove.forEach((item) => {
                      try {
                        writer.remove(item)
                      } catch {
                        // ignore
                      }
                    })
                  })
                } catch {
                  // ignore
                }
              }

              const handlePaste = (event: any) => {
                const cb = onPasteFilesRef.current
                const data = event.clipboardData as ClipboardEvent['clipboardData'] | null
                const clipboardFiles: File[] = []
                let plainText = ''

                if (data) {
                  plainText = data.getData('text/plain') || ''

                  const fileList = data.files as FileList | null
                  if (fileList && fileList.length > 0) {
                    for (let i = 0; i < fileList.length; i += 1) {
                      const file = fileList.item(i)
                      if (file && typeof file.size === 'number' && file.size > 0) {
                        clipboardFiles.push(file)
                      }
                    }
                  }

                  if (!clipboardFiles.length) {
                    const items = (data.items || []) as any
                    if (items && items.length > 0) {
                      for (let i = 0; i < items.length; i += 1) {
                        const item = items[i]
                        if (!item) continue
                        const kind = typeof item.kind === 'string' ? item.kind : null
                        const type = typeof item.type === 'string' ? item.type : ''
                        if (kind === 'file') {
                          const file =
                            typeof item.getAsFile === 'function' ? item.getAsFile() : null
                          if (file && typeof file.size === 'number' && file.size > 0) {
                            clipboardFiles.push(file as File)
                          }
                        } else if (!kind && type.startsWith('image/')) {
                          const file =
                            typeof item.getAsFile === 'function' ? item.getAsFile() : null
                          if (file && typeof file.size === 'number' && file.size > 0) {
                            clipboardFiles.push(file as File)
                          }
                        }
                      }
                    }
                  }
                }

                // Quando um callback de arquivos foi fornecido (caso do chat),
                // evitamos que o CKEditor processe o paste padrão (para não inserir imagens)
                // e tratamos manualmente anexos + texto plano, incluindo URLs de imagens.
                if (cb) {
                  const hasImageUrl =
                    !!plainText &&
                    /(https?:\/\/[^\s]+?\.(?:png|jpe?g|gif|webp))(?![^\s])/i.test(
                      plainText,
                    )
                  if (!clipboardFiles.length && !hasImageUrl) {
                    return
                  }

                  event.preventDefault()
                  try {
                    if (typeof event.stopImmediatePropagation === 'function') {
                      event.stopImmediatePropagation()
                    }
                  } catch {
                    // ignore
                  }
                  try {
                    if (typeof event.stopPropagation === 'function') {
                      event.stopPropagation()
                    }
                  } catch {
                    // ignore
                  }

                  // 1) Anexos vindos diretamente do clipboard (prints, etc.)
                  if (clipboardFiles.length) {
                    try {
                      cb(clipboardFiles)
                    } catch {
                      // silencioso
                    }
                  }

                  // 2) Detecta URLs de imagem no texto colado (Slack-like)
                  const imageUrls: string[] = []
                  let textForEditor = plainText

                  if (textForEditor) {
                    const imgUrlRegex =
                      /(https?:\/\/[^\s]+?\.(?:png|jpe?g|gif|webp))(?![^\s])/gi

                    textForEditor = textForEditor.replace(imgUrlRegex, (match) => {
                      imageUrls.push(match)
                      return ''
                    })

                    textForEditor = textForEditor.replace(/\s{2,}/g, ' ').trim()
                  }

                  if (imageUrls.length) {
                    ;(async () => {
                      const fetchedFiles: File[] = []

                      for (const url of imageUrls) {
                        try {
                          const res = await fetch(url)
                          if (!res.ok) continue
                          const blob = await res.blob()
                          if (!blob.type || !blob.type.startsWith('image/')) continue

                          const urlObj = new URL(url)
                          const name = urlObj.pathname.split('/').filter(Boolean).pop() || 'image'
                          const file = new File([blob], name, { type: blob.type })
                          fetchedFiles.push(file)
                        } catch {
                          // ignora URLs que não puderem ser carregadas
                        }
                      }

                      if (fetchedFiles.length) {
                        try {
                          cb(fetchedFiles)
                        } catch {
                          // silencioso
                        }
                      }
                    })()
                  }

                  // 3) Insere apenas o texto "limpo" (sem URLs de imagem) no editor
                  if (textForEditor && editorRef.current) {
                    try {
                      const editor: any = editorRef.current
                      editor.model.change((writer: any) => {
                        const insertPosition =
                          editor.model.document.selection.getFirstPosition()
                        writer.insertText(textForEditor, insertPosition)
                      })
                    } catch {
                      // silencioso
                    }
                  }

                  // Fallback: se o CKEditor inseriu imagens inline antes do nosso handler
                  // (ex.: paste em editor vazio), removemos imediatamente para evitar
                  // o "preview grande" dentro do editor. O chat deve exibir anexos
                  // apenas como thumbnails externos.
                  removeInlineImagesIfAny()
                  setTimeout(removeInlineImagesIfAny, 0)

                  return
                }
              }

              editableEl.addEventListener('paste', handlePaste as any, true)
              detachPasteListener = () => {
                try {
                  editableEl.removeEventListener('paste', handlePaste as any, true)
                } catch {
                  // ignore
                }
              }

              const handleDrop = (event: any) => {
                const cb = onPasteFilesRef.current
                if (!cb) return
                const data = event.dataTransfer as DataTransfer | null
                if (!data) return

                const fileList = data.files as FileList | null
                const files: File[] = []

                if (fileList && fileList.length > 0) {
                  for (let i = 0; i < fileList.length; i += 1) {
                    const file = fileList.item(i)
                    if (file && typeof file.size === 'number' && file.size > 0) {
                      files.push(file)
                    }
                  }
                }

                if (!files.length) return

                event.preventDefault()
                try {
                  if (typeof event.stopImmediatePropagation === 'function') {
                    event.stopImmediatePropagation()
                  }
                } catch {
                  // ignore
                }
                try {
                  if (typeof event.stopPropagation === 'function') {
                    event.stopPropagation()
                  }
                } catch {
                  // ignore
                }
                try {
                  cb(files)
                } catch {
                  // silencioso
                }

                removeInlineImagesIfAny()
                setTimeout(removeInlineImagesIfAny, 0)
              }

              editableEl.addEventListener('drop', handleDrop as any, true)
              detachDropListener = () => {
                try {
                  editableEl.removeEventListener('drop', handleDrop as any, true)
                } catch {
                  // ignore
                }
              }
            }
          } catch {
            // silencioso
          }

          editorRef.current.model.document.on('change:data', () => {
            try {
              onChange(editorRef.current.getData())
            } catch {
              // ignore
            }
          })

          // Enter: envia mensagem quando configurado
          try {
            const viewDocument = editorRef.current?.editing?.view?.document
            if (viewDocument && typeof viewDocument.on === 'function') {
              viewDocument.on('keydown', (evt: any, data: any) => {
                const handler = onEnterPressRef.current
                if (!handler) return

                const keyCode = data?.keyCode ?? data?.domEvent?.keyCode
                const shiftKey = data?.shiftKey ?? data?.domEvent?.shiftKey

                if (keyCode !== 13) return

                const isInsideListItem = (() => {
                  try {
                    const editor = editorRef.current
                    const selection = editor?.model?.document?.selection
                    const position = selection?.getFirstPosition?.()
                    let parent = position?.parent || null

                    while (parent) {
                      if (typeof parent.getAttribute === 'function') {
                        const listType = parent.getAttribute('listType')
                        const listItemId = parent.getAttribute('listItemId')
                        if (listType || listItemId) return true
                      }

                      if (
                        typeof parent.is === 'function' &&
                        parent.is('element', 'listItem')
                      ) {
                        return true
                      }

                      parent = parent.parent || null
                    }
                  } catch {
                    // ignore
                  }
                  return false
                })()

                // Em modo chat (Enter envia), precisamos permitir listas funcionarem:
                // - Dentro de uma lista: Enter deve criar um novo item, não enviar a mensagem.
                // - Shift+Enter: tentamos converter para Enter (Slack-like) para criar um novo item.
                if (shiftKey && isInsideListItem) {
                  try {
                    const editor = editorRef.current
                    const enterCmd = editor?.commands?.get?.('enter')
                    if (enterCmd) {
                      if (typeof data?.preventDefault === 'function') {
                        data.preventDefault()
                      }
                      if (evt && typeof evt.stop === 'function') {
                        evt.stop()
                      }
                      try {
                        editor.editing?.view?.focus()
                      } catch {
                        // ignore
                      }
                      try {
                        editor.execute('enter')
                      } catch {
                        // ignore
                      }
                      try {
                        editor.editing?.view?.focus()
                      } catch {
                        // ignore
                      }
                    }
                  } catch {
                    // ignore
                  }
                  return
                }

                if (shiftKey) return

                if (typeof data?.preventDefault === 'function') {
                  data.preventDefault()
                }
                if (evt && typeof evt.stop === 'function') {
                  evt.stop()
                }

                handler()
              })
            }
          } catch {
            // Se algo falhar no binding de teclado, ignoramos silenciosamente
          }

          // Notifica consumidores avançados
          try {
            if (onReadyRef.current) {
              onReadyRef.current(editorRef.current)
            }
          } catch {
            // silencioso
          }
        } catch {
          // criação do editor falhou
        }
      }
    })()

    return () => {
      cancelled = true
      try {
        detachPasteListener?.()
      } catch {
        // ignore
      }
      try {
        detachDropListener?.()
      } catch {
        // ignore
      }
      try {
        editorRef.current?.destroy?.()
      } catch {
        // ignore
      }
      try {
        const host = toolbarHostRef.current
        if (host) {
          while (host.firstChild) host.removeChild(host.firstChild)
        }
      } catch {
        // ignore
      }
    }
  }, [])

  React.useEffect(() => {
    try {
      if (ready && editorRef.current && editorRef.current.getData() !== value) {
        editorRef.current.setData(value || '')
      }
    } catch {
      // ignore
    }
  }, [value, ready])

  React.useEffect(() => {
    if (ready) return
    const el = fallbackTextareaRef.current
    if (!el) return

    const minHeight = typeof height === 'number' && Number.isFinite(height) ? height : 0
    if (!minHeight) return

    const maxHeight = Math.max(minHeight * 3, minHeight + 120)

    try {
      el.style.height = 'auto'
      el.style.minHeight = `${minHeight}px`
      el.style.maxHeight = `${maxHeight}px`
      const next = Math.min(el.scrollHeight, maxHeight)
      el.style.height = `${Math.max(next, minHeight)}px`
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
    } catch {
      // ignore
    }
  }, [value, ready, height])

  return (
    <div className={className}>
      <div ref={toolbarHostRef} className="ck-toolbar-host" />
      <div
        ref={ref}
        style={{
          minHeight: ready ? height : 0,
          background: 'var(--background)',
          color: 'var(--foreground)',
        }}
      />
      {!ready && (
        <div>
          <textarea
            ref={fallbackTextareaRef}
            rows={1}
            className="w-full resize-none rounded-md border border-input bg-background p-2 text-sm text-foreground"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              const handler = onEnterPressRef.current
              if (!handler) return
              if (e.key !== 'Enter') return
              if (e.shiftKey) return
              e.preventDefault()
              handler()
            }}
            style={{ minHeight: height }}
            placeholder={placeholder || 'Digite os termos aqui...'}
          />
          {initError && (
            <div className="mt-1 text-[11px] text-red-400">{initError}</div>
          )}
        </div>
      )}
    </div>
  )
}
